import { Component, OnInit } from "@angular/core";
import { NavParams, ViewController } from "ionic-angular";
import { OrderTypeConfiguration } from "models/order-management/models";
import { StatefulOrderResponse } from "models/order-management/statefullOrderResponse";

interface PatientSummary {
  patientName: string;
  patientId: number;
  count: number;
}

interface OrderTypeSummary {
  orderType: string;
  orderTypeDisplayName: string;
  count: number;
}

@Component({
  selector: "external-export-order-modal",
  templateUrl: "external-export-order-modal.html",
})
export class ExternalExportOrderModalComponent implements OnInit {
  selectedOrders: StatefulOrderResponse[] = [];
  orderTypeConfigurations: OrderTypeConfiguration[] = [];
  patientSummaries: PatientSummary[] = [];
  orderTypeSummaries: OrderTypeSummary[] = [];
  totalCount: number = 0;
  orderNotes: string = "";
  confirmationChecked: boolean = false;

  constructor(private viewCtrl: ViewController, private navParams: NavParams) {}

  ngOnInit() {
    this.selectedOrders = this.navParams.get("selectedOrders") || [];
    this.orderTypeConfigurations =
      this.navParams.get("orderTypeConfigurations") || [];
    this.calculateSummaries();
  }

  calculateSummaries() {
    // Group by patient (externalPatientChartId)
    const patientMap = new Map<number, { name: string; count: number }>();

    this.selectedOrders.forEach((order) => {
      const patientId = order.externalPatientChartId;
      const patientName = order.patientName;

      if (patientMap.has(patientId)) {
        patientMap.get(patientId).count++;
      } else {
        patientMap.set(patientId, { name: patientName, count: 1 });
      }
    });

    // Convert to array for display
    this.patientSummaries = Array.from(patientMap.entries()).map(
      ([patientId, data]) => ({
        patientName: data.name,
        patientId: patientId,
        count: data.count,
      })
    );

    // Group by order type
    const orderTypeMap = new Map<string, number>();

    this.selectedOrders.forEach((order) => {
      const orderType = order.orderType;

      if (orderTypeMap.has(orderType)) {
        orderTypeMap.set(orderType, orderTypeMap.get(orderType) + 1);
      } else {
        orderTypeMap.set(orderType, 1);
      }
    });

    // Convert to array for display with proper display names
    this.orderTypeSummaries = Array.from(orderTypeMap.entries()).map(
      ([orderType, count]) => ({
        orderType: orderType,
        orderTypeDisplayName: this.getOrderTypeDisplayName(orderType),
        count: count,
      })
    );

    // Calculate total count
    this.totalCount = this.selectedOrders.length;
  }

  getOrderTypeDisplayName(orderType: string): string {
    // Find the order type configuration for this order type
    const orderTypeConfiguration = this.orderTypeConfigurations.find(
      (config) => config.orderType === orderType
    );

    if (orderTypeConfiguration && orderTypeConfiguration.orderTypeDisplayName) {
      return orderTypeConfiguration.orderTypeDisplayName;
    }

    // Fallback: convert VISIT_FREQUENCY to "Visit Frequency"
    return orderType
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  cancel() {
    this.viewCtrl.dismiss({ cancelled: true });
  }

  send() {
    // CO-3361 r2: gate on the SAME condition the Send button is disabled by.
    // This checked only the attestation while the button checked `canSend`
    // (attestation AND a non-blank note), so any path reaching send() other
    // than that button exported with no note. A guard is only worth what its
    // weakest entry point is.
    if (this.canSend) {
      const exportData = {
        selectedOrders: this.selectedOrders,
        orderNotes: this.orderNotes,
        patientSummaries: this.patientSummaries,
        orderTypeSummaries: this.orderTypeSummaries,
        totalCount: this.totalCount,
      };

      this.viewCtrl.dismiss({ success: true, data: exportData });
    }
  }

  /**
   * Both the attestation AND a real note are required before this export can
   * be sent. Coerced to a genuine boolean — the previous expression returned
   * the empty STRING when the note was blank, which the declared `: boolean`
   * return type quietly misreported.
   */
  get canSend(): boolean {
    return !!(
      this.confirmationChecked &&
      this.orderNotes &&
      this.orderNotes.trim().length > 0
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/order-managment/external-export-order-modal/external-export-order-modal.ts